/**
 * Тема Mini App — палитра Onboardix, светлая или тёмная по телефону.
 *
 * Решение владелицы: цвета берём из токенов ERP (`--color-*` в
 * styles/index.css), а не из темы Telegram. От Telegram берём только
 * признак «светлая/тёмная» (`colorScheme` — он приходит вместе с событием
 * `themeChanged`), а без него — системную настройку `prefers-color-scheme`.
 *
 * Тема ставится атрибутом `data-theme` на <html> — тем же, что у ERP, —
 * но БЕЗ записи в localStorage: выбор темы в самом ERP (`onboardix_theme`)
 * Mini App не трогает. При снятии оболочки прежний атрибут возвращается.
 *
 * После смены темы шапка и фон Telegram красятся цветом `--color-bg`
 * текущей темы, чтобы на границе с приложением не было шва.
 */

import { useEffect } from 'react'

import {
    getTelegramColorScheme,
    onTelegramThemeChanged,
    paintTelegramChrome,
} from './telegramWebApp'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function systemPrefersDark() {
    try {
        return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
    } catch {
        return false
    }
}

export function resolveTmaScheme() {
    return getTelegramColorScheme() || (systemPrefersDark() ? 'dark' : 'light')
}

function toHex(value) {
    const raw = (value || '').trim()
    if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase()
    const short = raw.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i)
    if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase()
    const rgb = raw.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i)
    if (rgb) {
        return `#${rgb.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, '0')).join('')}`
    }
    return ''
}

/** Цвет токена Onboardix (`--color-*`) для темы, что сейчас стоит на <html>. */
export function readBrandColor(token) {
    try {
        return toHex(getComputedStyle(document.documentElement).getPropertyValue(token))
    } catch {
        return ''
    }
}

/** Цвет фона Onboardix для темы, что сейчас стоит на <html>. */
export function readBrandBackground() {
    return readBrandColor('--color-bg')
}

export function applyTmaScheme() {
    const scheme = resolveTmaScheme()
    document.documentElement.setAttribute('data-theme', scheme)
    paintTelegramChrome(readBrandBackground())
    return scheme
}

export default function useTmaBrandTheme(enabled = true) {
    useEffect(() => {
        if (!enabled) return undefined
        const root = document.documentElement
        const previous = root.getAttribute('data-theme')
        applyTmaScheme()

        const offTelegram = onTelegramThemeChanged(applyTmaScheme)
        let media = null
        try {
            media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null
            media?.addEventListener?.('change', applyTmaScheme)
        } catch {
            media = null
        }

        return () => {
            offTelegram()
            media?.removeEventListener?.('change', applyTmaScheme)
            if (previous === null) root.removeAttribute('data-theme')
            else root.setAttribute('data-theme', previous)
        }
    }, [enabled])
}
